import React, { useState, useEffect } from 'react';
import { statsApi, hospitalApi, paymentApi } from '../services/api';

interface DashboardStats {
  totalHospitals: number;
  activeHospitals: number;
  totalRevenue: number;
  yearlyRevenue: number;
  monthlyRevenue: number;
}

interface Hospital {
  hospitalId: string;
  name: string;
  subscriptionAmount: number;
  currency: string;
  isActive: boolean;
  totalPaid: number;
  registrationDate: string;
  paymentStatusOverride?: 'paid' | 'partial' | 'pending' | 'overdue' | null;
}

// Helper to get current month in YYYY-MM format
const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Helper to determine payment status for a hospital for current month
const getPaymentStatus = (hospital: Hospital, payments: any[]): { status: string; class: string } => {
  // Check for manual override first
  if (hospital.paymentStatusOverride) {
    const statusMap: Record<string, { status: string; class: string }> = {
      paid: { status: 'Paid', class: 'paid' },
      partial: { status: 'Partial', class: 'partial' },
      pending: { status: 'Pending', class: 'pending' },
      overdue: { status: 'Overdue', class: 'overdue' },
    };
    return statusMap[hospital.paymentStatusOverride] || { status: 'Pending', class: 'pending' };
  }

  const currentMonth = getCurrentMonth();
  const monthPayments = payments.filter(
    p => p.hospitalId === hospital.hospitalId && p.month === currentMonth
  );
  
  const totalPaidThisMonth = monthPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const monthlyDue = hospital.subscriptionAmount || 0;
  
  if (totalPaidThisMonth >= monthlyDue) {
    return { status: 'Paid', class: 'paid' };
  } else if (totalPaidThisMonth > 0) {
    return { status: 'Partial', class: 'partial' };
  } else {
    return { status: 'Pending', class: 'pending' };
  }
};

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalHospitals: 0,
    activeHospitals: 0,
    totalRevenue: 0,
    yearlyRevenue: 0,
    monthlyRevenue: 0,
  });
  const [recentHospitals, setRecentHospitals] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load stats, hospitals, and payments in parallel
      const [statsData, hospitals, paymentsData] = await Promise.all([
        statsApi.getDashboard(),
        hospitalApi.getAll(),
        paymentApi.getAll(),
      ]);

      // Map backend stats fields to frontend expected fields
      const mappedStats: DashboardStats = {
        totalHospitals: hospitals.length,
        activeHospitals: hospitals.filter((h: any) => h.isActive).length,
        totalRevenue: statsData.totalAllTime || 0,
        yearlyRevenue: statsData.totalCurrentYear || 0,
        monthlyRevenue: statsData.totalCurrentMonth || 0,
      };

      setStats(mappedStats);
      setPayments(paymentsData);
      setRecentHospitals(hospitals.slice(0, 5)); // Show last 5 hospitals
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={loadDashboardData} className="btn-retry">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>
          <h2>Financial Overview</h2>
          <p className="page-subtitle">Track revenue streams and hospital payment performance</p>
        </div>
        <div className="header-actions">
          <button className="btn-date">
            📅 {currentMonth}
          </button>
          <button className="btn-configure" onClick={loadDashboardData}>
            🔄 Refresh
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon revenue-icon">💰</div>
          <div className="stat-content">
            <p className="stat-label">Total Revenue</p>
            <h3 className="stat-value">{formatCurrency(stats.totalRevenue)}</h3>
            <p className="stat-description">All time revenue</p>
          </div>
          <div className="stat-trend positive">↗ YTD</div>
        </div>

        <div className="stat-card">
          <div className="stat-icon hospitals-icon">🏥</div>
          <div className="stat-content">
            <p className="stat-label">Active Hospitals</p>
            <h3 className="stat-value">{stats.activeHospitals}</h3>
            <p className="stat-description">of {stats.totalHospitals} total</p>
          </div>
          <div className="stat-trend positive">↗ Active</div>
        </div>

        <div className="stat-card">
          <div className="stat-icon monthly-icon">📊</div>
          <div className="stat-content">
            <p className="stat-label">Monthly Revenue</p>
            <h3 className="stat-value">{formatCurrency(stats.monthlyRevenue)}</h3>
            <p className="stat-description">This month</p>
          </div>
          <div className="stat-trend positive">↗ Monthly</div>
        </div>

        <div className="stat-card">
          <div className="stat-icon yearly-icon">📈</div>
          <div className="stat-content">
            <p className="stat-label">Yearly Revenue</p>
            <h3 className="stat-value">{formatCurrency(stats.yearlyRevenue)}</h3>
            <p className="stat-description">{new Date().getFullYear()} total</p>
          </div>
          <div className="stat-trend positive">↗ {new Date().getFullYear()}</div>
        </div>
      </div>

      <div className="dashboard-sections">
        <div className="section-card">
          <div className="section-header">
            <h3>Recent Hospitals</h3>
            <a href="/hospitals" className="view-all">View All →</a>
          </div>
          <div className="hospitals-list">
            {recentHospitals.length === 0 ? (
              <div className="empty-state">
                <p>No hospitals registered yet.</p>
                <a href="/hospitals" className="btn-primary">Add First Hospital</a>
              </div>
            ) : (
              recentHospitals.map((hospital) => {
                const paymentStatus = getPaymentStatus(hospital, payments);
                return (
                  <div key={hospital.hospitalId} className="hospital-item">
                    <div className="hospital-info">
                      <span className="hospital-name">{hospital.name}</span>
                      <span className="hospital-subscription">
                        {formatCurrency(hospital.subscriptionAmount)}/month
                      </span>
                    </div>
                    <div className="hospital-badges">
                      <span className={`status-badge payment-${paymentStatus.class}`}>
                        {paymentStatus.status}
                      </span>
                      <span className={`status-badge ${hospital.isActive ? 'active' : 'inactive'}`}>
                        {hospital.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="section-card">
          <div className="section-header">
            <h3>Quick Actions</h3>
          </div>
          <div className="quick-actions">
            <a href="/hospitals" className="action-btn">
              <span className="action-icon">🏥</span>
              <span>Add Hospital</span>
            </a>
            <a href="/payments" className="action-btn">
              <span className="action-icon">💳</span>
              <span>Record Payment</span>
            </a>
            <button onClick={loadDashboardData} className="action-btn">
              <span className="action-icon">📊</span>
              <span>Refresh Data</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
